/* eslint-disable no-console -- this module is the single allowed sink for browser technical logs */
import { SeverityLevel } from './types';

/** localStorage key: set to "1" to enable verbose (debug/info) logs in production builds. */
export const ATLAS_DEBUG_LOGS_KEY = 'atlas_debug_logs';

export type LogContext = Record<string, unknown>;

/**
 * Returns true when debug/info logs should be emitted.
 * Opt-in only: set localStorage `atlas_debug_logs` to "1" (works in dev and prod builds).
 * Development is not auto-verbose so simulations and map work stay quiet by default.
 */
export function isVerboseLoggingEnabled(): boolean {
  try {
    if (typeof localStorage === 'undefined') return false;
    return localStorage.getItem(ATLAS_DEBUG_LOGS_KEY) === '1';
  } catch {
    return false;
  }
}

function formatLine(level: SeverityLevel, message: string, context: LogContext): string {
  const ts = new Date().toISOString();
  const category = typeof context.category === 'string' ? context.category : undefined;
  const rest: LogContext = { ...context };
  delete rest.category;
  const hasRest = Object.keys(rest).length > 0;
  const prefix = category ? `[${ts}] [${level.toUpperCase()}] [${category}]` : `[${ts}] [${level.toUpperCase()}]`;
  return hasRest ? `${prefix} ${message}` : `${prefix} ${message}`;
}

function emitConsole(level: SeverityLevel, message: string, context: LogContext): void {
  const line = formatLine(level, message, context);
  const { category: _c, ...rest } = context;
  const payload = Object.keys(rest).length > 0 ? rest : undefined;

  switch (level) {
    case SeverityLevel.Debug:
      if (payload !== undefined) console.debug(line, payload);
      else console.debug(line);
      break;
    case SeverityLevel.Info:
      if (payload !== undefined) console.info(line, payload);
      else console.info(line);
      break;
    case SeverityLevel.Warning:
      if (payload !== undefined) console.warn(line, payload);
      else console.warn(line);
      break;
    case SeverityLevel.Error:
    case SeverityLevel.Critical:
      if (payload !== undefined) console.error(line, payload);
      else console.error(line);
      break;
    default:
      if (payload !== undefined) console.log(line, payload);
      else console.log(line);
  }
}

/**
 * Technical application logging (not product analytics).
 * - debug / info: only when `localStorage.atlas_debug_logs === "1"`.
 * - warn / error / critical: always emitted.
 */
export class LoggingService {
  private static instance: LoggingService;

  private constructor() {}

  public static getInstance(): LoggingService {
    if (!LoggingService.instance) {
      LoggingService.instance = new LoggingService();
    }
    return LoggingService.instance;
  }

  /**
   * Low-level log with explicit severity. Prefer debug/info/warn/error/critical.
   */
  public log(message: string, level: SeverityLevel, extra: LogContext = {}): void {
    if (level === SeverityLevel.Debug || level === SeverityLevel.Info) {
      if (!isVerboseLoggingEnabled()) return;
    }
    emitConsole(level, message, extra);
  }

  public debug(message: string, extra: LogContext = {}): void {
    if (!isVerboseLoggingEnabled()) return;
    emitConsole(SeverityLevel.Debug, message, extra);
  }

  public info(message: string, extra: LogContext = {}): void {
    if (!isVerboseLoggingEnabled()) return;
    emitConsole(SeverityLevel.Info, message, extra);
  }

  public warn(message: string, extra: LogContext = {}): void {
    emitConsole(SeverityLevel.Warning, message, extra);
  }

  public error(message: string, extra: LogContext = {}): void {
    emitConsole(SeverityLevel.Error, message, extra);
  }

  public critical(message: string, extra: LogContext = {}): void {
    emitConsole(SeverityLevel.Critical, message, extra);
  }
}

/** Singleton for convenient imports in services and components. */
export const loggingService = LoggingService.getInstance();
